import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../../App/redux/store';
import {
  createEntity,
  deleteEntity,
  updateEntity,
} from '../../../services/axiosInstanceService';
import { appManagerActions } from '../../../store/appManager/actions';
import { loaderActions } from '../../../store/loader/actions';
import { modalsActions } from '../../modals/modalsSlice';
import { folderParser } from '../foldersParser';
import { folderNotifications } from '../notifications';
import { fetchFolders } from './foldersRequests';

/**
 * Thunk action to fetch folders for the authenticated user.
 *
 * @async
 * @function
 * @param {void} params - No parameters are expected.
 * @param {object} thunkAPI - The thunk API object, including `getState`.
 * @param {Function} thunkAPI.getState - Function to get the current state.
 * @returns {Promise<Folders>} A promise that resolves to the fetched and parsed folder data.
 * @throws {Error} If fetching folders fails.
 */
const fetchFoldersThunk = createAsyncThunk(
  'folders/fetchFoldersThunk',
  async (params: void, { getState }) => {
    try {
      // Retrieve the current application state
      const state = getState() as RootState;

      // Extract user ID and collaborator list from the state
      const { userIdIri } = state.auth;
      const { users } = state.users;

      // Fetch the folders associated with the user
      const result = await fetchFolders(userIdIri, users.result, userIdIri);

      // Return the fetched folder data
      return result;
    } catch (error) {
      // Reject the promise if an error occurs
      return Promise.reject(error);
    }
  },
);

/**
 * Thunk action to create a new folder with specified collaborators.
 *
 * @async
 * @function
 * @param {Object} params - The parameters for creating the folder.
 * @param {string} params.name - The name of the new folder.
 * @param {string[]} params.collaborators - Array of collaborator user IDs.
 * @param {object} thunkAPI - The thunk API object, including `dispatch` and `getState`.
 * @param {Function} thunkAPI.dispatch - Redux dispatch function.
 * @param {Function} thunkAPI.getState - Function to get the current state.
 * @returns {Promise<any>} A promise that resolves to the newly created folder data.
 * @throws {Error} If required parameters are missing or the folder creation fails.
 */
const createFolderThunk = createAsyncThunk(
  'folders/createFolderThunk',
  async (
    params: {
      body: { name: string; collaborators: NestedCollaboratorIdIris };
      navigate?: any;
    },
    { dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const { users } = state.users;
      const { userIdIri } = state.auth;

      if (!users.result) throw new Error('Users not found');
      if (!userIdIri) throw new Error('AuthUser not found');

      const { body } = params;
      // Check for required parameters
      if (!body.name) throw new Error('No folder name found');
      if (!body.collaborators) throw new Error('No collaborator array found');

      // Show loader while the request is in progress
      dispatch(loaderActions.loaderDisplay.visible());

      // Send request to create the folder
      const result = await createEntity({ endpoint: '/folders', body });

      // Parse the response into a usable format
      const parsed = folderParser(result, users.result, userIdIri);

      // Hide loader after the request completes
      dispatch(loaderActions.loaderDisplay.hidden());
      params.navigate?.('/mes-dossiers');
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          folderNotifications.create().success(),
        ),
      );
      return parsed;
    } catch (error) {
      // Hide the loader in case of an error
      dispatch(loaderActions.loaderDisplay.hidden());
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          folderNotifications.create().failure(),
        ),
      );
      return Promise.reject(error);
    }
  },
);

const updateFolderThunk = createAsyncThunk(
  'folders/updateFolderThunk',
  async (
    params: {
      folderIdIri: string;
      body: { name: string; collaborators: NestedCollaboratorIdIris };
      navigate: any;
    },
    { dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const { userIdIri } = state.auth;
      const { updateFolder } = state.modals;
      const { users } = state.users;

      if (!users.result) throw new Error('Users not found');
      if (!userIdIri) throw new Error('AuthUser not found');
      if (!params.folderIdIri) throw new Error('Folder idIri not found');
      if (!params.body) throw new Error('Folder body not found');

      dispatch(loaderActions.loaderDisplay.visible());
      const body = {
        name: params.body.name,
        collaborators: params.body.collaborators,
      };

      const result = await updateEntity({ idIri: params.folderIdIri, body });

      const parsed = folderParser(result, users.result, userIdIri);

      dispatch(loaderActions.loaderDisplay.hidden());
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          folderNotifications.update(parsed.name).success(),
        ),
      );
      if (updateFolder) {
        dispatch(modalsActions.updateFolder(null));
      } else {
        params.navigate?.('/mes-dossiers');
      }
      return parsed;
    } catch (error) {
      dispatch(loaderActions.loaderDisplay.hidden());
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          folderNotifications.update(params.body.name ?? '').failure(),
        ),
      );

      return Promise.reject(error);
    }
  },
);

const deleteFolderThunk = createAsyncThunk(
  'folders/deleteFolderThunk',
  async (params: { folder: IFolder | null }, { dispatch }) => {
    try {
      if (!params.folder) throw new Error('no folder idIri found');
      dispatch(loaderActions.loaderDisplay.visible());
      dispatch(modalsActions.deleteFolder(null));
      const result = await deleteEntity(params.folder.idIri);

      if (result === 204) {
        dispatch(
          appManagerActions.actionNotificationDisplay.visible(
            folderNotifications.delete(params.folder.name).success(),
          ),
        );
        dispatch(loaderActions.loaderDisplay.hidden());
        return params.folder;
      } else {
        throw new Error('Bad status for delete folder');
      }
    } catch (error) {
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          folderNotifications.delete(params.folder?.name ?? '').failure(),
        ),
      );
      dispatch(loaderActions.loaderDisplay.hidden());
      return Promise.reject(error);
    }
  },
);
export {
  createFolderThunk,
  deleteFolderThunk,
  fetchFoldersThunk,
  updateFolderThunk,
};
